import type { Node, RichContent } from 'ricos-schema';
import { Node_Type } from 'ricos-schema';

import { ensureNodeType, toHtml } from '../../converters/html/toHtml/toHtml';
import { firstRight } from '../../fp-utils';
import { extract } from '../../RicosContentAPI/extract';
import type {
  DataFieldNamesWithContainerData,
  NonTextualNode,
  NonTextualTranslatable,
  TextualNode,
  TextualTranslatable,
  Translatable,
} from '../types';
import { getDataField, isType } from '../utils';
import { fieldsToTranslatables } from './fieldsToTranslatables';
import {
  toAppEmbedTranslatable,
  toButtonTranslatable,
  toGalleryTranslatable,
  toHtmlTranslatable,
  toImageTranslatable,
  toLinkPreviewTranslatable,
  toPollTranslatable,
  toVideoTranslatable,
} from './pluginsToTranslatables';

export const toTranslatables = ({ nodes }: RichContent) => {
  const nodesWithType = ensureNodeType(nodes ?? []);
  return [
    ...nodeDataTranslatables(nodesWithType),
    ...nodeContainerDataTranslatables(nodesWithType),
  ];
};

const nodeDataTranslatables = (nodes: Node[]): Translatable[] =>
  extract(nodes)
    .map(node =>
      firstRight(node, [] as Translatable[], [
        [isType(Node_Type.HEADING), getTextualContent],
        [isType(Node_Type.PARAGRAPH), getTextualContent],
        [isType(Node_Type.APP_EMBED), toAppEmbedTranslatable],
        [isType(Node_Type.BUTTON), toButtonTranslatable],
        [isType(Node_Type.GALLERY), toGalleryTranslatable],
        [isType(Node_Type.HTML), toHtmlTranslatable],
        [isType(Node_Type.IMAGE), toImageTranslatable],
        [isType(Node_Type.LINK_PREVIEW), toLinkPreviewTranslatable],
        [isType(Node_Type.POLL), toPollTranslatable],
        [isType(Node_Type.VIDEO), toVideoTranslatable],
      ])
    )
    .get()
    .flat();

const nodeContainerDataTranslatables = (nodes: Node[]): NonTextualTranslatable[] =>
  extract(nodes)
    .filter(node => !!node[getDataField(node)]?.containerData)
    .map((node: NonTextualNode) => {
      const pluginData = getDataField(node) as DataFieldNamesWithContainerData;
      return [
        ...fieldsToTranslatables(node, [pluginData, 'containerData', 'spoiler', 'buttonText']),
        ...fieldsToTranslatables(node, [pluginData, 'containerData', 'spoiler', 'description']),
      ];
    })
    .get()
    .flat();

const getTextualContent = (node: TextualNode): TextualTranslatable[] => {
  const { id, type } = node;
  const textNodes = extract(node)
    .filter(({ nodes }) => (nodes?.length ?? 0) > 0)
    .get();
  if (textNodes.length === 0) {
    return [];
  }
  return [
    {
      id,
      type,
      text: toHtml({
        nodes: textNodes,
      }),
    },
  ];
};
